/**
 * Line icons for the setup wizard, drawn on a 24-unit grid with
 * `currentColor` so they take the surrounding text colour in both themes.
 */
import type { ReactNode } from 'react';

function Svg({ children, size = 24 }: { children: ReactNode; size?: number }): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

export const Icon = {
  restaurant: (p: { size?: number }) => (
    <Svg {...p}>
      <path d="M7 3v8M4.5 3v5a2.5 2.5 0 0 0 5 0V3M7 11v10" />
      <path d="M17 21V3c-2.2 1.3-3.5 3.8-3.5 7v3.5H17" />
    </Svg>
  ),
  retail: (p: { size?: number }) => (
    <Svg {...p}>
      <path d="M5 8h14l-1.2 12H6.2L5 8Z" />
      <path d="M9 10V6.5a3 3 0 0 1 6 0V10" />
    </Svg>
  ),
  salon: (p: { size?: number }) => (
    <Svg {...p}>
      <circle cx="6.5" cy="17.5" r="2.5" />
      <circle cx="17.5" cy="17.5" r="2.5" />
      <path d="M8.3 15.7 18 4M15.7 15.7 6 4" />
    </Svg>
  ),
  laundry: (p: { size?: number }) => (
    <Svg {...p}>
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <path d="M4 7.5h16" />
      <circle cx="12" cy="14" r="4" />
      <path d="M7 5.3h.01M9.5 5.3h.01" />
    </Svg>
  ),
  check: (p: { size?: number }) => (
    <Svg {...p}>
      <path d="m5 12.5 4.5 4.5L19 7.5" />
    </Svg>
  ),
  arrow: (p: { size?: number }) => (
    <Svg {...p}>
      <path d="M5 12h14M13 6l6 6-6 6" />
    </Svg>
  ),
  upload: (p: { size?: number }) => (
    <Svg {...p}>
      <path d="M12 16V4M7 9l5-5 5 5M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" />
    </Svg>
  ),
  globe: (p: { size?: number }) => (
    <Svg {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c2.5 2.6 3.8 5.6 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.6-3.8-9S9.5 5.6 12 3Z" />
    </Svg>
  ),
  mail: (p: { size?: number }) => (
    <Svg {...p}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3.5 6.5 8.5 6.5 8.5-6.5" />
    </Svg>
  ),
  lock: (p: { size?: number }) => (
    <Svg {...p}>
      <rect x="4.5" y="10.5" width="15" height="10" rx="2" />
      <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
    </Svg>
  ),
  tables: (p: { size?: number }) => (
    <Svg {...p}>
      <path d="M3 9h18M5 9v10M19 9v10M8 9l1-4h6l1 4" />
    </Svg>
  ),
  kitchen: (p: { size?: number }) => (
    <Svg {...p}>
      <rect x="3" y="4" width="18" height="12" rx="2" />
      <path d="M8 20h8M12 16v4M7 8h6M7 11h10" />
    </Svg>
  ),
  delivery: (p: { size?: number }) => (
    <Svg {...p}>
      <path d="M3 6h11v10H3zM14 10h4l3 3v3h-7" />
      <circle cx="7" cy="17.5" r="1.8" />
      <circle cx="17.5" cy="17.5" r="1.8" />
    </Svg>
  ),
  callCenter: (p: { size?: number }) => (
    <Svg {...p}>
      <path d="M4 13v-1a8 8 0 0 1 16 0v1" />
      <rect x="3" y="13" width="4" height="6" rx="1.5" />
      <rect x="17" y="13" width="4" height="6" rx="1.5" />
      <path d="M19 19c0 1.5-2 2-5 2" />
    </Svg>
  ),
  externalOrders: (p: { size?: number }) => (
    <Svg {...p}>
      <rect x="6" y="2.5" width="12" height="19" rx="2.5" />
      <path d="M10 18.5h4M9.5 8.5l2 2 3.5-3.5" />
    </Svg>
  ),
  shipping: (p: { size?: number }) => (
    <Svg {...p}>
      <path d="m12 3 8 4.5v9L12 21l-8-4.5v-9L12 3Z" />
      <path d="m4 7.5 8 4.5 8-4.5M12 12v9" />
    </Svg>
  ),
  inventory: (p: { size?: number }) => (
    <Svg {...p}>
      <path d="M3 10 12 4l9 6v10H3V10Z" />
      <path d="M7 20v-6h10v6M7 17h10" />
    </Svg>
  ),
  appointments: (p: { size?: number }) => (
    <Svg {...p}>
      <rect x="3.5" y="5" width="17" height="15" rx="2" />
      <path d="M3.5 10h17M8 3v4M16 3v4M8 14h3" />
    </Svg>
  ),
  onlineBooking: (p: { size?: number }) => (
    <Svg {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </Svg>
  ),
  qrMenu: (p: { size?: number }) => (
    <Svg {...p}>
      <rect x="3.5" y="3.5" width="7" height="7" rx="1" />
      <rect x="13.5" y="3.5" width="7" height="7" rx="1" />
      <rect x="3.5" y="13.5" width="7" height="7" rx="1" />
      <path d="M14 14h2.5v2.5H14zM18 18h2.5v2.5H18zM18 14h2.5M14 18v2.5" />
    </Svg>
  ),
  barcode: (p: { size?: number }) => (
    <Svg {...p}>
      <path d="M4 5v14M7 5v14M10 5v14M14 5v14M17 5v14M20 5v14" />
    </Svg>
  ),
  salesStaff: (p: { size?: number }) => (
    <Svg {...p}>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M3 20c.6-3.4 3-5.5 6-5.5s5.4 2.1 6 5.5M16 6.5a3 3 0 0 1 0 5.5M18 14.5c1.7.7 2.8 2.6 3 5.5" />
    </Svg>
  ),
} as const;

export type IconName = keyof typeof Icon;
